import { useValuationsQuery } from '@dealstack/frontend/app/pages/Valuations/queries/useValuationsQuery';
import { useCalculationWorkflow } from '@dealstack/frontend/app/pages/Valuations/queries/useCalculationWorkflow';
import { usePersonInputsQuery } from '@dealstack/frontend/app/pages/Valuations/queries/usePersonInputsQuery';
import { useHistoricalAndCustomFXRatesQuery } from '@dealstack/frontend/app/pages/Valuations/queries/useHistoricalAndCustomFXRatesQuery';
import { useValuationBreadcrumbs } from '@dealstack/frontend/app/pages/Valuations/hooks/useValuationBreadcrumbs';
import {
	SourcesUsesHeaderText,
	SourcesAndUsesWrapper,
	SourcesUsesHeader,
	SourcesUsesHeaderItem,
	SourcesUsesTableHeader,
	SourcesUsesTableWrapper,
	PadLeft,
	SourcesUsesFooter,
	SourcesUsesFooterItem,
} from '@dealstack/frontend/app/pages/Valuations/views/SourcesAndUses/styled';
import { TextV2 } from '@dealstack/frontend/shared/components/TextV2/TextV2';
import { PersonInputFormContainer } from './components/PersonInputFormContainer';
import { ProceedsTable } from '@dealstack/frontend/app/pages/Valuations/views/SourcesAndUses/components/ProceedsTable';
import { DistributionsTable } from '@dealstack/frontend/app/pages/Valuations/views/SourcesAndUses/components/DistributionsTable';
import { AlignRight } from '@dealstack/frontend/app/pages/Valuations/components/styled';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import {
	ValuationsLayout,
	TitleContainer,
	ValuationsLoaderContainer,
} from '@dealstack/frontend/app/pages/Valuations/styled';
import { useValuationNavigation } from '@dealstack/frontend/app/pages/Valuations/hooks/useValuationNavigation';
import { usePersonSelection } from '@dealstack/frontend/app/pages/Valuations/hooks/usePersonSelection';
import { CurrencyType } from '@dealstack/shared/lib/generated';
import { SourcesUsesCategories } from '@dealstack/frontend/app/pages/Valuations/views/SourcesAndUses/shared/SourcesUsesTable';
import { useMemo } from 'react';
import { useTranslationValuations } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	formatSummaryDecimal,
	formatSummaryDecimalString,
} from '@dealstack/frontend/app/pages/Valuations/components/table/utils/formatting';
import { Button, Flex, Text } from '@mantine/core';
import { EntitySelectorDropdown } from '@dealstack/frontend/app/pages/Valuations/components/EntitySelectorDropdown/EntitySelectorDropdown';
import { ValuationLocation } from '@dealstack/frontend/app/pages/Valuations/shared/valuationRoutes';
import { AutoSaveIndicator } from '@dealstack/frontend/app/pages/Valuations/components/AutoSaveIndicator/AutoSaveIndicator';
import { ValuationsWarnings } from '@dealstack/frontend/app/pages/Valuations/components/ValuationsWarnings/ValuationsWarnings';
import { LinearSolveWarning } from '@dealstack/frontend/app/pages/Valuations/components/LinearSolveWarning/LinearSolveWarning';
import { InPageNavValuationsHeaderContainer } from '@dealstack/frontend/app/pages/Valuations/views/ValuationsHome/styled';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag';
import { HighPrecisionDecimal } from '@dealstack/shared/lib/format/currency/highPrecisionDecimal';
import { VALUATION_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';

export const ValuationSourcesAndUses = () => {
	const { pathValuationId, dealId, navigateTo } = useValuationNavigation();
	const { valuations } = useValuationsQuery(dealId);
	const currentValuation = valuations.find((v) => v.id === pathValuationId);

	const {
		summaryByPerson: summaryData,
		reloadSummary,
		isSummaryFirstLoad,
		validationErrors,
		isSummaryLoading,
		linearSolve,
	} = useCalculationWorkflow(dealId, pathValuationId);

	const tValuations = useTranslationValuations();
	const { value: inPageFeatureFlag } = useDealstackFeatureFlag(
		FEATURE_FLAG.IN_PAGE_BREADCRUMBS_AND_QUICK_SWITCH_MENU
	);

	const {
		selectedPersonId,
		selectedMipId,
		selectedEntity,
		personOptions,
		availableTags,
		loadPerson,
		selectedEntityName,
		pathPersonId,
		pathMipId,
	} = usePersonSelection({
		summaryByPersonData: summaryData,
		view: ValuationLocation.SOURCES_AND_USES,
		isSummaryFirstLoad,
	});

	const {
		personInputs,
		isLoading: isLoadingPersonInputs,
		saveFields,
		deleteField,
		flushPersonInput,
		getPersonInputsByCategory,
	} = usePersonInputsQuery(
		dealId,
		currentValuation?.id,
		selectedPersonId,
		selectedMipId,
		reloadSummary
	);

	const baseCurrency = currentValuation?.defaultCurrency ?? CurrencyType.EUR;

	const { modifiedRates: fxRates } = useHistoricalAndCustomFXRatesQuery(
		dealId,
		currentValuation?.id,
		currentValuation?.defaultCurrency
	);

	const selectedGroup = useMemo(() => {
		return selectedEntity?.groups.find((g) => {
			if (selectedMipId) {
				return g.id === selectedMipId;
			}
			return !g.id;
		});
	}, [selectedEntity, selectedMipId]);

	const undistributedCash = useMemo(() => {
		const selectedEntityData = selectedEntity?.groups.find(
			(group) => group.id === selectedMipId
		);
		return new HighPrecisionDecimal(selectedEntityData?.retained.total ?? 0).sub(
			selectedEntityData?.retained.cash ?? 0
		);
	}, [selectedEntity, selectedMipId]);

	const sourcesTotal = formatSummaryDecimalString(selectedGroup?.proceeds.total ?? '0');
	const usesTotal = formatSummaryDecimal(
		undistributedCash.add(selectedGroup?.outflows.total ?? 0)
	);

	const categories = useMemo(
		() => [
			{
				id: SourcesUsesCategories.Receivables,
				title: tValuations('sources_and_uses.receivables'),
				nameColumnTitle: 'Name',
				amountColumnTitle: 'Input Amount',
			},
			{
				id: SourcesUsesCategories.Payables,
				title: tValuations('sources_and_uses.payables'),
				nameColumnTitle: 'Name',
				amountColumnTitle: 'Input Amount',
			},
			{
				id: SourcesUsesCategories.CashOnBalanceSheet,
				title: tValuations('sources_and_uses.cash_on_balance_sheet'),
				nameColumnTitle: 'Name',
				amountColumnTitle: 'Input Amount',
			},
			{
				id: SourcesUsesCategories.RetainedCashAndHoldbacks,
				title: tValuations('sources_and_uses.retained_cash_and_holdbacks'),
				nameColumnTitle: 'Name',
				amountColumnTitle: 'Input Amount',
			},
		],
		[tValuations]
	);

	useValuationBreadcrumbs(
		{
			view: 'sources-and-uses',
			dealId: dealId ?? '',
			valuation: currentValuation ?? undefined,
			personId: pathPersonId,
			mipId: pathMipId,
			selectedEntityName,
		},
		inPageFeatureFlag
	);

	if (!summaryData || isSummaryFirstLoad) {
		return (
			<ValuationsLoaderContainer>
				<InlineLoader withoutBackground />
			</ValuationsLoaderContainer>
		);
	}

	if (selectedEntity) {
		return (
			<ValuationsLayout>
				<InPageNavValuationsHeaderContainer>
					<TitleContainer>
						<EntitySelectorDropdown
							selectedPersonId={selectedPersonId}
							selectedMipId={selectedMipId}
							personOptions={personOptions}
							selectedEntity={selectedEntity}
							availableTags={availableTags}
							isLoadingPerson={isLoadingPersonInputs}
							loadPerson={loadPerson}
						/>
						<AutoSaveIndicator />
					</TitleContainer>
					<Flex align="center" gap="xs">
						<Button
							data-testid={
								VALUATION_ELEMENT_ID.SOURCES_AND_USES_BREAKPOINT_SUMMARY_BTN
							}
							variant="default"
							onClick={() =>
								navigateTo(ValuationLocation.BREAKPOINT_SUMMARY, {
									dealId: dealId ?? '',
									valuationId: currentValuation?.id ?? '',
									personId: selectedPersonId,
									mipId: selectedMipId,
								})
							}
						>
							<Text size="B6Medium" c="dark.9">
								{tValuations('sources_and_uses.breakpoint_summary_button')}
							</Text>
						</Button>
						<ValuationsWarnings
							validationErrors={validationErrors}
							isSummaryLoading={isSummaryLoading}
						/>
					</Flex>
				</InPageNavValuationsHeaderContainer>
				<Flex direction="column" gap="1rem" mih={0}>
					<LinearSolveWarning status={linearSolve} />
					<SourcesUsesHeader>
						<SourcesUsesHeaderItem>
							<PadLeft>
								<TextV2 variant="sans/14/600">
									{tValuations('sources_and_uses.sources')}
								</TextV2>
							</PadLeft>
							<AlignRight>
								<SourcesUsesHeaderText variant="sans/14/600">
									{tValuations('sources_and_uses.input_amount')}
								</SourcesUsesHeaderText>
							</AlignRight>
							<AlignRight>
								<SourcesUsesHeaderText variant="sans/14/600">
									{tValuations('sources_and_uses.converted_amount', {
										currency: currentValuation?.defaultCurrency,
									})}
								</SourcesUsesHeaderText>
							</AlignRight>
							<TextV2 variant="sans/14/600"></TextV2>
						</SourcesUsesHeaderItem>
						<SourcesUsesHeaderItem>
							<PadLeft>
								<TextV2 variant="sans/14/600">
									{tValuations('sources_and_uses.uses')}
								</TextV2>
							</PadLeft>
							<AlignRight>
								<SourcesUsesHeaderText variant="sans/14/600">
									{tValuations('sources_and_uses.input_amount')}
								</SourcesUsesHeaderText>
							</AlignRight>
							<AlignRight>
								<SourcesUsesHeaderText variant="sans/14/600">
									{tValuations('sources_and_uses.converted_amount', {
										currency: currentValuation?.defaultCurrency,
									})}
								</SourcesUsesHeaderText>
							</AlignRight>
							<SourcesUsesHeaderText variant="sans/14/600"></SourcesUsesHeaderText>
						</SourcesUsesHeaderItem>
					</SourcesUsesHeader>

					<SourcesAndUsesWrapper>
						<SourcesUsesTableWrapper
							data-testid={VALUATION_ELEMENT_ID.SOURCES_AND_USES_PROCEEDS_SECTION}
						>
							<SourcesUsesTableHeader>
								<TextV2 variant="sans/14/600">
									{tValuations('sources_and_uses.proceeds')}
								</TextV2>
							</SourcesUsesTableHeader>
							<ProceedsTable
								person={selectedEntity}
								summary={summaryData}
								baseCurrency={baseCurrency}
								fxRates={fxRates}
							/>
						</SourcesUsesTableWrapper>

						<SourcesUsesTableWrapper
							data-testid={VALUATION_ELEMENT_ID.SOURCES_AND_USES_DISTRIBUTION_SECTION}
						>
							<SourcesUsesTableHeader>
								<TextV2 variant="sans/14/600">
									{tValuations('sources_and_uses.distributions')}
								</TextV2>
							</SourcesUsesTableHeader>
							<DistributionsTable
								person={selectedEntity}
								summary={summaryData}
								baseCurrency={baseCurrency}
								fxRates={fxRates}
								isSummaryLoading={isSummaryLoading}
							/>
						</SourcesUsesTableWrapper>

						{categories.map((category) => (
							<PersonInputFormContainer
								key={category.id}
								category={category.title}
								categoryId={category.id}
								selectedEntity={selectedEntity}
								summaryData={summaryData}
								formId={`form-${category.id}`}
								nameColumnTitle={category.nameColumnTitle}
								amountColumnTitle={category.amountColumnTitle}
								entityId={selectedEntity.id}
								mipId={selectedMipId}
								baseCurrency={baseCurrency}
								fxRates={fxRates}
								validationErrors={validationErrors}
								personInputs={personInputs}
								isLoadingPerson={isLoadingPersonInputs}
								saveFields={saveFields}
								deleteField={deleteField}
								flushPersonInput={flushPersonInput}
								getPersonInputsByCategory={getPersonInputsByCategory}
							/>
						))}
					</SourcesAndUsesWrapper>

					<SourcesUsesFooter>
						<SourcesUsesFooterItem>
							<PadLeft>
								<TextV2 variant="sans/14/600">
									{tValuations('sources_and_uses.sources_total')}
								</TextV2>
							</PadLeft>
							<div></div>
							<AlignRight
								data-testid={
									VALUATION_ELEMENT_ID.SOURCES_AND_USES_FOOTER_SECTION_TOTAL_SOURCES
								}
							>
								<TextV2 variant="sans/14/600">{sourcesTotal}</TextV2>
							</AlignRight>
							<div></div>
						</SourcesUsesFooterItem>
						<SourcesUsesFooterItem>
							<PadLeft>
								<TextV2 variant="sans/14/600">
									{tValuations('sources_and_uses.uses_total')}
								</TextV2>
							</PadLeft>
							<div></div>
							<AlignRight
								data-testid={
									VALUATION_ELEMENT_ID.SOURCES_AND_USES_FOOTER_SECTION_TOTAL_USES
								}
							>
								<TextV2 variant="sans/14/600">{usesTotal}</TextV2>
							</AlignRight>
							<div></div>
						</SourcesUsesFooterItem>
					</SourcesUsesFooter>
				</Flex>
			</ValuationsLayout>
		);
	}

	return (
		<Flex justify="center" align="center" h="100%" w="100%">
			{tValuations('sources_and_uses.no_entity_selected')}
		</Flex>
	);
};
